/**
 * @file 複数の資料のページの対応をまとめて取得する hook。
 *
 * @what: 資料 id の並びを受け、id ごとに対応を読んで Map にする。取れなかった資料は
 *   対応が無いものとして扱う (関係するページが出ないだけで、読む操作は止めない)。
 * @why: 「関係するページ」は、自分の対応 (このページ → 相手のページ) と、自分を指す資料の対応
 *   (相手のページ → このページ) の両方を使う。どちらも資料を開いたときだけ取る。
 * @failure: 取得失敗は console に出し、その資料は空の対応にする。
 */

import { useEffect, useState } from "react";
import { fetchSourcePageLinks, type StudySourcePageLink } from "../api/sourcePageLinks";

const EMPTY: ReadonlyMap<string, StudySourcePageLink[]> = new Map();

export function useSourcePageLinks(
  familyId: string,
  sourceIds: readonly string[],
): ReadonlyMap<string, StudySourcePageLink[]> {
  const [map, setMap] = useState<ReadonlyMap<string, StudySourcePageLink[]>>(EMPTY);
  const key = sourceIds.join("\n");

  useEffect(() => {
    const ids = key === "" ? [] : key.split("\n");
    setMap(EMPTY);
    if (ids.length === 0) return;
    const controller = new AbortController();
    Promise.all(
      ids.map((id) =>
        fetchSourcePageLinks(familyId, id, controller.signal).catch((err: unknown) => {
          if (!controller.signal.aborted)
            console.error("[study] failed to load source page links", err);
          return [] as StudySourcePageLink[];
        }),
      ),
    ).then((lists) => {
      if (controller.signal.aborted) return;
      setMap(new Map(ids.map((id, i) => [id, lists[i] ?? []])));
    });
    return () => controller.abort();
  }, [familyId, key]);

  return map;
}
